import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { Section, SectionHead } from "@/components/landing/Section";
import { TROLLS, isFestival, nextWindow } from "@/lib/trolls";

const useFestivals = () =>
  useMemo(() => {
    const map = new Map();
    TROLLS.filter(isFestival).forEach((tr) => {
      const f = map.get(tr.festival_name) || { name: tr.festival_name, start: tr.window_start, end: tr.window_end, trolls: [] };
      f.trolls.push(tr.name);
      map.set(tr.festival_name, f);
    });
    return [...map.values()]
      .map((f) => ({ ...f, win: nextWindow(f.start, f.end) }))
      .sort((a, b) => a.win.start - b.win.start);
  }, []);

export default function Specials() {
  const { t, fmtDate } = useLang();
  const festivals = useFestivals();
  return (
    <Section id="specials" testId="specials-section" className="bg-ink text-parchment">
      <SectionHead kicker={t.specials.kicker} title={<span className="text-parchment">{t.specials.title}</span>} />
      <p className="max-w-2xl mt-5 text-parchment/75 text-base md:text-lg leading-relaxed">{t.specials.body}</p>
      <div className="grid lg:grid-cols-12 gap-10 mt-14">
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4 content-start">
          {t.specials.flavours.map((f, i) => (
            <motion.div
              key={f.key}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              data-testid={`special-flavour-${f.key}`}
              className={`rounded-[20px] border p-6 ${f.key === "promo" ? "border-dashed border-gold/50" : "border-parchment/15"} bg-parchment/[0.04]`}
            >
              <div className="flex items-baseline gap-3">
                <span className="font-display text-4xl font-semibold text-gold tabular-nums">{f.count}</span>
                <h3 className="font-display text-lg font-semibold">{f.title}</h3>
              </div>
              <p className="text-sm text-parchment/70 mt-2 leading-relaxed">{f.body}</p>
            </motion.div>
          ))}
        </div>
        <div className="lg:col-span-5 rounded-[20px] bg-parchment text-ink p-6 md:p-8" data-testid="festival-calendar">
          <h3 className="font-display text-2xl font-semibold">{t.specials.calendar}</h3>
          <ul className="mt-5 divide-y divide-dashed divide-line">
            {festivals.map((f) => {
              const same = f.win.start.getTime() === f.win.end.getTime();
              return (
                <li key={f.name} className="py-3 flex items-start justify-between gap-4" data-testid={`festival-row-${f.start}`}>
                  <div>
                    <div className="font-bold">{t.festivals[f.name]}</div>
                    <div className="text-xs text-rowan font-bold mt-0.5">{f.trolls.join(" · ")}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm tabular-nums text-ink-muted">
                      {fmtDate(f.win.start)}{!same && <><br />– {fmtDate(f.win.end)}</>}
                    </div>
                    {f.win.live && <span className="stamp !text-[10px] !py-0.5 mt-1 inline-block">{t.specials.liveNow}</span>}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </Section>
  );
}
